import React, { Component, useContext, useReducer, useEffect } from "react";
import shortid from "shortid";
import {
  ViewerContext,
  DatasourceContext,
  CacheContext,
  ProgressContext
} from "./MolstackContexts";
import request from "superagent";

let CANCELLED = Symbol("cancelled");

function elementLoaderReducer(state, action) {
  // Will update state during loading progress
  // Expecting action.step and action.status
  return {
    ...state,
    [action.step]: {
      ...state[action.step],
      status: action.status,
      error: action.status === "FAILURE" ? action.payload : false
    }
  };
}

export function useElementLoader(
  url,
  type,
  Fetcher,
  Preparer,
  Loader,
  Finalizer,
  Remover,
  dispatchProgress
) {
  const [state, dispatch] = useReducer(elementLoaderReducer, {
    FETCH: {},
    PREPARE: {},
    LOAD: {},
    url: url
  });

  function dispatchWithProgress(step, status, payload) {
    dispatch({ step, status, payload });
    dispatchProgress({ step, status, key: url });
  }

  useEffect(
    function load() {
      let didCancel = false;
      function createCancellable(step, Worker, options = {}) {
        return async function wrapped(input) {
          const work = Worker(input, url, options); //use url as key for optional caching
          dispatchWithProgress(step, "INIT", work);
          try {
            const result = await work;
            if (!didCancel) {
              dispatchWithProgress(step, "SUCCESS", result);
              return result;
            } else {
              return Promise.reject(CANCELLED);
            }
          } catch (error) {
            if (!didCancel) {
              dispatchWithProgress(step, "FAILURE", error);
              throw error;
            } else {
              dispatchProgress({ step, status: "CANCELLED", key: url });
              return Promise.reject(CANCELLED);
            }
          }
        };
      }

      const fetchData = createCancellable("FETCH", Fetcher, { type });
      const prepareData = createCancellable("PREPARE", Preparer);
      const loadData = createCancellable("LOAD", Loader);
      const finalize = createCancellable("FINALIZE", Finalizer);

      fetchData(url)
        .then(prepareData)
        .then(loadData)
        .then(finalize)
        .catch(error => {
          if (error === CANCELLED) {
            const action_type = "CANCELLED";
            dispatchProgress({ type: action_type, key: url });
            // Do nothing
          } else {
            throw error;
            // Do nothing as well
          }
        });

      return function cancel() {
        didCancel = true;
        Remover(url);
        // Cleanup
      };
    },
    [url, Fetcher, Preparer, Loader]
  );
  return state;
} // Its immutable, we use it once. Each component gets recreated for url

export function MolstackModel(props) {
  const modelProps = [];

  const viewer = useContext(ViewerContext); //This is viewer instance yey
  const datasource = useContext(DatasourceContext);
  const dispatchProgress = useContext(ProgressContext);

  function useProperty(name) {
    //TODO: use other id than url, to display same model using differrent options
    viewer.setModelProperty(props.url, name, props[name]);
  }

  const state = useElementLoader(
    props.url,
    "model",
    datasource,
    viewer.prepareModel,
    viewer.loadModel,
    () => modelProps.forEach(name => setProperty(name)), //Finalize
    viewer.removeModel,
    dispatchProgress
  );

  modelProps.forEach(name => useEffect(() => setProperty(name), [props[name]]));

  return (
    (viewer && viewer.renderModelDOM && viewer.renderModelDOM(state)) || null
  );
}

export function MolstackMap(props) {
  const mapProps = ["color", "rmsd", "difference", "hidden"];

  const viewer = useContext(ViewerContext); //This is viewer instance yey
  const datasource = useContext(DatasourceContext);
  const dispatchProgress = useContext(ProgressContext);
  // Load data
  function setProperty(name) {
    //TODO: consider using pattern viewer.map[id].setColor()
    //TODO: consider using useRef to hold the map around?
    viewer.setMapProperty(props.url, name, props[name]);
  }

  const state = useElementLoader(
    props.url,
    "map",
    datasource,
    viewer.prepareMap,
    viewer.loadMap,
    () => mapProps.forEach(name => setProperty(name)), //Finalize
    viewer.removeMap,
    dispatchProgress
  );

  mapProps.forEach(name => useEffect(() => setProperty(name), [props[name]]));

  return (viewer && viewer.renderMapDOM && viewer.renderMapDOM(state)) || null;
}
